package co.maytes.standupbot.dashboard.components.feedback

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Shadow
import co.maytes.standupbot.dashboard.styles.Spacing
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px

@Composable
fun SkeletonLine(widthPct: Int = 100, heightPx: Int = 12, radiusPx: Int = 6) {
    Box(
        modifier = SkeletonPulseStyle.toModifier()
            .width(widthPct.percent)
            .height(heightPx.px)
            .backgroundColor(Palette.surfaceSubtle)
            .styleModifier { property("border-radius", radiusPx.toString() + "px") },
    )
}

@Composable
fun SkeletonBox(widthPx: Int, heightPx: Int, radiusPx: Int = 8) {
    Box(
        modifier = SkeletonPulseStyle.toModifier()
            .width(widthPx.px)
            .height(heightPx.px)
            .backgroundColor(Palette.surfaceSubtle)
            .styleModifier {
                property("border-radius", radiusPx.toString() + "px")
                property("flex-shrink", "0")
            },
    )
}

@Composable
fun SkeletonCircle(sizePx: Int = 36) {
    Box(
        modifier = SkeletonPulseStyle.toModifier()
            .width(sizePx.px)
            .height(sizePx.px)
            .backgroundColor(Palette.surfaceSubtle)
            .styleModifier {
                property("border-radius", "9999px")
                property("flex-shrink", "0")
            },
    )
}

@Composable
fun SkeletonBlock(heightPx: Int, widthPct: Int = 100, radiusPx: Int = 10) {
    Box(
        modifier = SkeletonPulseStyle.toModifier()
            .width(widthPct.percent)
            .height(heightPx.px)
            .backgroundColor(Palette.surfaceSubtle)
            .styleModifier { property("border-radius", radiusPx.toString() + "px") },
    )
}

@Composable
fun SkeletonCard(modifier: Modifier = Modifier, content: @Composable () -> Unit) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(Spacing.lg)
            .backgroundColor(Palette.surface)
            .borderRadius(Radius.md)
            .styleModifier {
                property("border", "1px solid " + Palette.border.cssRgb())
                property("box-shadow", Shadow.XS)
            }
            .then(modifier),
    ) { content() }
}

@Composable
fun SkeletonStatCard() {
    SkeletonCard(
        modifier = Modifier.styleModifier {
            property("height", "100%")
            property("min-height", "128px")
            property("justify-content", "space-between")
        },
    ) {
        Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(Spacing.xs)) {
            SkeletonBox(widthPx = 30, heightPx = 30, radiusPx = 9)
            SkeletonLine(widthPct = 38, heightPx = 10)
        }
        Box(modifier = Modifier.styleModifier { property("margin-top", "14px") }) {
            SkeletonBox(widthPx = 76, heightPx = 30, radiusPx = 8)
        }
        Box(modifier = Modifier.styleModifier { property("margin-top", "12px") }) {
            SkeletonLine(widthPct = 52, heightPx = 9)
        }
    }
}

@Composable
fun SkeletonStatRow(count: Int = 3) {
    Row(
        modifier = Modifier.fillMaxWidth().gap(Spacing.md).styleModifier {
            property("flex-wrap", "wrap")
            property("align-items", "stretch")
        },
    ) {
        repeat(count) {
            Box(modifier = Modifier.styleModifier {
                property("flex", "1 1 240px")
                property("display", "flex")
            }) { SkeletonStatCard() }
        }
    }
}

@Composable
fun SkeletonListRow(showAvatar: Boolean = false) {
    SkeletonCard(modifier = Modifier.styleModifier { property("margin-bottom", "12px") }) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(Spacing.sm).styleModifier { property("min-width", "0") }) {
                if (showAvatar) SkeletonCircle(sizePx = 34) else SkeletonBox(widthPx = 56, heightPx = 22, radiusPx = 999)
                Column(modifier = Modifier.gap(Spacing.xs)) {
                    SkeletonBox(widthPx = 150, heightPx = 12, radiusPx = 6)
                    SkeletonBox(widthPx = 96, heightPx = 9, radiusPx = 6)
                }
            }
            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(Spacing.lg)) {
                SkeletonBox(widthPx = 88, heightPx = 12, radiusPx = 6)
                SkeletonBox(widthPx = 56, heightPx = 12, radiusPx = 6)
            }
        }
    }
}

@Composable
fun SkeletonList(rows: Int = 5, showAvatar: Boolean = false) {
    Column(modifier = Modifier.fillMaxWidth()) {
        repeat(rows) { SkeletonListRow(showAvatar = showAvatar) }
    }
}

@Composable
fun SkeletonFormFields(fields: Int = 3) {
    Column(modifier = Modifier.fillMaxWidth().gap(Spacing.lg)) {
        repeat(fields) {
            Column(modifier = Modifier.fillMaxWidth().gap(Spacing.xs)) {
                SkeletonLine(widthPct = 22, heightPx = 10)
                SkeletonBlock(heightPx = 40, radiusPx = 8)
            }
        }
    }
}
